import React, { useEffect, useState, useMemo } from "react";
import { Layout, ChevronDown, ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import { useLazyGetScreenQuery, useLazyGetBannerDashboardQuery } from "../../../redux/actions/Admin/ProductAdmin";

const AdminAdBanner: React.FC = () => {
    const navigate = useNavigate();
    const [selectedScreenOpt, setSelectedScreenOpt] = useState<Option | null>(null);

    // API Query Hooks
    const [triggerGetScreens, { data: screenList }] = useLazyGetScreenQuery();
    const [triggerGetDashboard, { data: dashboardData, isFetching: isFetchingDashboard }] = useLazyGetBannerDashboardQuery();

    useEffect(() => {
        triggerGetScreens();
    }, [triggerGetScreens]);

    const screenOptions: Option[] = useMemo(() => {
        return screenList?.map((s: any) => ({ id: String(s.id), label: String(s.name), value: String(s.id) })) || [];
    }, [screenList]);

    useEffect(() => {
        if (selectedScreenOpt) {
            triggerGetDashboard(Number(selectedScreenOpt.value));
        }
    }, [selectedScreenOpt, triggerGetDashboard]);

    return (
        <div className="min-h-screen max-w-7xl mx-auto px-4 py-6 space-y-6 text-left">
            
            {/* TOP HEADER */}
            <div className="border-b border-slate-100 pb-5">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                    <Layout className="text-[#2F5E2E] w-5 h-5 stroke-[2.5]" />
                    App Banner Settings
                </h1>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">
                    Select an application screen view to customize layouts and promotional banners.
                </p>
            </div>

            {/* SCREEN SELECTOR DROPDOWN */}
            <div className="max-w-md bg-white p-4 rounded-2xl border border-slate-200/80 shadow-3xs">
                <Dropdown 
                    options={screenOptions} 
                    value={selectedScreenOpt} 
                    onChange={(opt) => setSelectedScreenOpt(opt)} 
                    showLabel
                    label="Target App Screen" 
                    dropdownIcon={<ChevronDown size={14} />}
                    innerClassName="bg-slate-50 border-slate-200 text-xs font-bold"
                />
            </div>

            {/* SECTIONS GRID */}
            {isFetchingDashboard ? (
                <div className="py-24 text-center flex flex-col items-center justify-center gap-2 text-xs font-bold text-slate-400 italic">
                    <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
                    <span>Loading screen sections...</span>
                </div>
            ) : dashboardData?.sections && dashboardData.sections.length > 0 ? (
                <div className="space-y-4">
                    <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider">Available UI Placement Zones</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {dashboardData.sections.map((section) => (
                            <div 
                                key={section.sectionId}
                                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col justify-between shadow-3xs hover:border-[#6DBE45] transition group relative overflow-hidden"
                            >
                                <div className="space-y-2">
                                    <div className="flex items-start justify-between">
                                        <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md font-mono">
                                            {section.layoutType}
                                        </span>
                                        <span className="text-[10px] font-black text-[#2F5E2E] bg-emerald-50 px-2 py-0.5 rounded-md">
                                            {section.banners?.length || 0} Banners Active
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-slate-900 group-hover:text-[#2F5E2E] transition">
                                            {section.displayName || "Unnamed Section"}
                                        </h4>
                                        <p className="text-[11px] text-slate-400 font-medium line-clamp-2 mt-0.5">
                                            {section.description || "No description set for this section layout."}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                                    <div className="text-[10px] text-slate-400 font-semibold font-mono">
                                        Max Items: <span className="text-slate-700 font-bold">{section.maxItems || "Unlimited"}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate("adBannerInnerSection", { 
                                            state: { 
                                                screenId: Number(selectedScreenOpt?.value),
                                                sectionId: section.sectionId,
                                                sectionData: section 
                                            } 
                                        })}
                                        className="inline-flex items-center gap-1.5 text-xs font-black text-slate-700 hover:text-white bg-slate-50 hover:bg-[#2F5E2E] px-3 py-1.5 rounded-xl border border-slate-200 transition cursor-pointer"
                                    >
                                        <span>Open Banners ({section.banners?.length || 0})</span>
                                        <ArrowRight size={12} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : selectedScreenOpt ? (
                <div className="bg-white border border-slate-200 border-dashed rounded-2xl p-16 text-center text-xs font-bold text-slate-400 italic">
                    No active layout components configured on this screen canvas yet.
                </div>
            ) : (
                <div className="p-12 border border-dashed rounded-2xl bg-white text-center text-slate-400 text-xs font-semibold italic">
                    Please pick an application screen dropdown option above to inspect template sections.
                </div>
            )}
        </div>
    );
};

export default AdminAdBanner;